import { PMSs } from 'main/hotelBrandsAndChains'
import moment from 'moment'
import { useEffect, useReducer } from 'react'

import { sanitizePepCsmActionFilterKeys } from '../PMS/derivePepCsmAction'

const { disconnected, ...pmsNamesExceptDisconnected } = PMSs // eslint-disable-line no-unused-vars

const defaultLocalStorageKey = 'health-check-filters'

/**
 * Initializes the state for the health check filters.
 * Tries to retrieve from localStorage based on provided key, falling back to default values.
 *
 * @param {string} key - The unique key for storing the filter state in localStorage.
 * @returns {Object} The initial state for the filter.
 */
const getInitialState = key => {
  const isPmsHealthCheckFilter = key === `${defaultLocalStorageKey}:pms`
  const defaultState = {
    hotelGroups: 'all',
    chains: 'all',
    brands: 'all',
    pms: [...Object.values(pmsNamesExceptDisconnected)],
    searchString: '',
    showTelcoDetails: false,
    showLowOutreaches: false,
    showLowConversions: false,
    hideZeroBalance: true,
    hideInvalidPaymentDate: true,
    paymentDateFrom: moment().subtract(30, 'days').format('YYYY-MM-DD'),
    paymentDateTo: moment().format('YYYY-MM-DD'),
    timeperiod: 30,
    periodStart: moment().add(-31, 'days').format('YYYY-MM-DD'),
    periodEnd: moment().add(-1, 'days').format('YYYY-MM-DD'),
    subsidiaryStatus: [
      'PendingPhoneNumberSetup',
      'PendingCampaignBrandRegistration',
      'BasicAccount',
      'MediumAccount',
      'LargeAccount',
      'AboutToLose'
    ],
    csmIds: [],
    inviteStatusFilter: 'all',
    // Status filters with default values - Alert and Warning enabled by default
    showAlert: true,
    showWarning: true,
    showGreen: isPmsHealthCheckFilter,
    pepHealthBadgeKeys: [
      'Working',
      'NeedsAttention',
      'AppNotInstalled',
      'PepAuthorizationError'
    ],
    pepOnboardingStates: ['Done', 'Pending'],
    pepFutureStates: ['Active', 'Inactive'],
    pepDesktopAppVersionFilter: 'all',
    pepCsmActions: []
  }

  const savedState = localStorage.getItem(key)
  if (savedState) {
    const parsed = JSON.parse(savedState)
    const isLegacyPmsStatusDefault =
      isPmsHealthCheckFilter &&
      parsed.showAlert === true &&
      parsed.showWarning === true &&
      parsed.showGreen === false
    // Ensure status filters have defaults if not present in saved state
    return {
      ...defaultState,
      ...parsed,
      csmIds: parsed.csmIds || [],
      pepListFiltersV2: true,
      // Override with defaults if status filters are undefined/null
      showAlert:
        parsed.showAlert !== undefined ? parsed.showAlert : defaultState.showAlert,
      showWarning:
        parsed.showWarning !== undefined ? parsed.showWarning : defaultState.showWarning,
      showGreen:
        isLegacyPmsStatusDefault
          ? true
          : parsed.showGreen !== undefined
          ? parsed.showGreen
          : defaultState.showGreen,
      pepHealthBadgeKeys: Array.isArray(parsed.pepHealthBadgeKeys)
        ? parsed.pepHealthBadgeKeys
        : defaultState.pepHealthBadgeKeys,
      pepOnboardingStates: Array.isArray(parsed.pepOnboardingStates)
        ? parsed.pepOnboardingStates
        : defaultState.pepOnboardingStates,
      pepFutureStates: Array.isArray(parsed.pepFutureStates)
        ? parsed.pepFutureStates
        : defaultState.pepFutureStates,
      pepDesktopAppVersionFilter:
        parsed.pepDesktopAppVersionFilter || defaultState.pepDesktopAppVersionFilter,
      pepCsmActions: isPmsHealthCheckFilter
        ? sanitizePepCsmActionFilterKeys(parsed.pepCsmActions)
        : defaultState.pepCsmActions
    }
  }

  return defaultState
}

function filterReducer(state, action) {
  switch (action.type) {
    case 'hotelGroups':
      return { ...state, hotelGroups: action.payload }
    case 'chains':
      return { ...state, chains: action.payload }
    case 'brands':
      return { ...state, brands: action.payload }
    case 'pms':
      return { ...state, pms: action.payload }
    case 'searchString':
      return { ...state, searchString: action.payload }
    case 'showTelcoDetails':
      return { ...state, showTelcoDetails: action.payload }
    case 'showLowOutreaches':
      return { ...state, showLowOutreaches: action.payload }
    case 'showLowConversions':
      return { ...state, showLowConversions: action.payload }
    case 'hideZeroBalance':
      return { ...state, hideZeroBalance: action.payload }
    case 'hideInvalidPaymentDate':
      return { ...state, hideInvalidPaymentDate: action.payload }
    case 'paymentDateFrom':
      return { ...state, paymentDateFrom: action.payload }
    case 'paymentDateTo':
      return { ...state, paymentDateTo: action.payload }
    case 'subsidiaryStatus':
      return { ...state, subsidiaryStatus: action.payload }
    case 'timePeriod':
      return {
        ...state,
        timeperiod: action.payload,
        periodStart: moment()
          .add(-action.payload - 1, 'days')
          .format('YYYY-MM-DD'),
        periodEnd: moment().add(-1, 'days').format('YYYY-MM-DD')
      }
    case 'csmIds':
      return { ...state, csmIds: action.payload }
    case 'inviteStatusFilter':
      return { ...state, inviteStatusFilter: action.payload }
    case 'showAlert':
      return { ...state, showAlert: action.payload }
    case 'showWarning':
      return { ...state, showWarning: action.payload }
    case 'showGreen':
      return { ...state, showGreen: action.payload }
    case 'pepHealthBadgeKeys':
      return { ...state, pepHealthBadgeKeys: action.payload }
    case 'pepOnboardingStates':
      return { ...state, pepOnboardingStates: action.payload }
    case 'pepFutureStates':
      return { ...state, pepFutureStates: action.payload }
    case 'pepDesktopAppVersionFilter':
      return { ...state, pepDesktopAppVersionFilter: action.payload }
    case 'pepCsmActions':
      return { ...state, pepCsmActions: sanitizePepCsmActionFilterKeys(action.payload) }
    default:
      throw new Error()
  }
}

/**
 * Custom hook for managing and persisting health check filters.
 *
 * @param {string} keySuffix - Suffix for the localStorage key to create unique keys per instance.
 * @returns {Array} State and dispatch method for the health check filters.
 */
function useHealthCheckFilters(keySuffix) {
  const storageKey = `${defaultLocalStorageKey}:${keySuffix}`
  const [state, dispatch] = useReducer(filterReducer, getInitialState(storageKey))

  // Save to localStorage whenever the state changes
  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(state))
  }, [state, storageKey])

  return [state, dispatch]
}

export default useHealthCheckFilters
